import React, { useEffect, useState } from 'react';
import ButtonAuth from '../../components/ButtonAuth';
import rocketBoyImg from '../../assets/images/barbfullLogo.png';
import Input from '../../components/Input';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';
import Alert from '../../components/Alert';
import config from '../../config';
import AuthWrapper from '../../components/AuthWrapper';
import AuthHeader from '../../components/header/AuthHeader';
import { useTrackingContext } from '../../context/trackingContext';
import BannerRight from '../../components/BannerRight';
import Footer from '../../components/Footer';
import { useUserContext } from '../../context/userContext';

function ParentalConsent() {
  const { trackFormStart, trackPageView, trackFormSubmit, age, trackError, dob } =
    useTrackingContext();
  const { referralId } = useUserContext();

  useEffect(() => {
    trackPageView({
      pageName: 'Parental Consent 13-15 page visit',
      accountCreationStep: 2.11
    });
    document.getElementById('left-container')?.scrollTo(0, 0);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { isSubmitting, isValid, isDirty }
  } = useForm({ mode: 'onTouched' });

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Parental Consent credentials form Start',
        accountCreationStep: 2.12
      });
  }, [isDirty]);

  const navigate = useNavigate();
  const client = useClient();
  const [showAlert, setShowAlert] = useState(false);

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'Parental Consent form Submit',
      accountCreationStep: 2.13,
      referralId
    });

    try {
      await client.post(`${config.everywhereApi}/users/pending-parental-consent`, {
        childName: data.name,
        childEmailAddress: data.email,
        dob,
        parentalEmail: data.parentalEmail,
        referralId
      });

      navigate('/parental-confirmation');
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: error.errorMessage });

      setShowAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  useEffect(() => {
    if (watch('email')) trigger('parentalEmail');
  }, [watch('email')]);

  useEffect(() => {
    if (!age) navigate('/create-account');
  }, []);

  return (
    <div className="flex h-screen">
      <div id="left-container" className="w-full overflow-y-scroll lg:w-1/2 relative left-form">
        <AuthHeader />
        <AuthWrapper className="max-w-sm mb-4">
          <form className="mb-6" autoComplete="false" onSubmit={handleSubmit(onSubmit)}>
            <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-5">
              Parental <br /> Consent
            </h1>

            <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-8" />

            <p className="font-Korolev-Light leading-6 text-base mb-5">
              To create your account, we must receive parental consent for those under the age of
              16. Please provide an email for us to contact your parent/guardian.
            </p>

            <div className="mb-4">
              <Controller
                defaultValue=""
                control={control}
                name="name"
                rules={{
                  required: {
                    value: true,
                    message: 'First Name is required'
                  }
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    type="text"
                    label="First Name"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    className="w-full"
                    error={error}
                  />
                )}
              />
            </div>

            <div className="mb-4">
              <Controller
                defaultValue=""
                control={control}
                name="email"
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Entered value does not match email format'
                  }
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    type="email"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Email Address"
                    className="w-full"
                    error={error}
                  />
                )}
              />
            </div>

            <div className="mb-4">
              <Controller
                defaultValue=""
                control={control}
                name="parentalEmail"
                rules={{
                  required: 'Parent/Guardian Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Entered value does not match email format'
                  },
                  validate: (value) => value !== watch('email') || 'Emails cant not match'
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    type="email"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Parent/Guardian's Email Address"
                    className="w-full"
                    error={error}
                  />
                )}
              />
            </div>

            <ButtonAuth
              label={'NOTIFY PARENT/GUARDIAN'}
              className="mb-3 lg:mb-7"
              type="submit"
              isSubmitting={isSubmitting}
              isDisabled={!isValid}
            />
          </form>
          {showAlert && (
            <Alert setShowAlert={setShowAlert} success={false}>
              <p className="pr-12 text-sm md:text-base">Error: {showAlert.text}</p>
            </Alert>
          )}
        </AuthWrapper>
        <Footer />
      </div>

      <BannerRight />
    </div>
  );
}

export default ParentalConsent;
